import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { App, Button, Form, Input, Result } from 'antd';
import { Controller, useForm } from 'react-hook-form';
import { authApi } from '../auth.api';
import { registerSchema, type RegisterForm } from '../auth.schemas';

export function RegisterPage() {
  const { message } = App.useApp();
  const form = useForm<RegisterForm>({
    resolver: zodResolver(registerSchema),
    defaultValues: { name: '', email: '', password: '' },
  });

  const mutation = useMutation({
    mutationFn: authApi.register,
    onError: (error) =>
      message.error(error instanceof Error ? error.message : 'Registration failed'),
  });

  if (mutation.isSuccess) {
    return (
      <Result
        status="success"
        title="Check your inbox"
        subTitle="We've sent a verification link to your email. Click it to activate your account."
      />
    );
  }

  return (
    <Form layout="vertical" onFinish={form.handleSubmit((v) => mutation.mutate(v))}>
      <Controller
        control={form.control}
        name="name"
        render={({ field, fieldState }) => (
          <Form.Item
            label="Name"
            validateStatus={fieldState.error ? 'error' : ''}
            help={fieldState.error?.message}
          >
            <Input {...field} placeholder="Your name" autoComplete="name" />
          </Form.Item>
        )}
      />
      <Controller
        control={form.control}
        name="email"
        render={({ field, fieldState }) => (
          <Form.Item
            label="Email"
            validateStatus={fieldState.error ? 'error' : ''}
            help={fieldState.error?.message}
          >
            <Input {...field} type="email" autoComplete="email" placeholder="you@example.com" />
          </Form.Item>
        )}
      />
      <Controller
        control={form.control}
        name="password"
        render={({ field, fieldState }) => (
          <Form.Item
            label="Password"
            validateStatus={fieldState.error ? 'error' : ''}
            help={
              fieldState.error?.message ?? 'At least 10 characters, with a letter and a number.'
            }
          >
            <Input.Password {...field} autoComplete="new-password" placeholder="••••••••" />
          </Form.Item>
        )}
      />
      <Button type="primary" htmlType="submit" block loading={mutation.isPending}>
        Create account
      </Button>
    </Form>
  );
}
